import React, { useState, useEffect, Fragment, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Paper,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Assessment as AssessmentIcon,
  ShowChart as ChartIcon,
  AccountCircle as AccountIcon,
  Chat as ChatIcon,
  Phone as PhoneIcon,
  Wallet as WalletIcon,
  Timeline as TimelineIcon,
  ViewColumn as ViewColumnIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  fetchDashboardMatrixMetricsApi,
  fetchUserLifecycleMetricsApi,
  fetchEngagementMetricsApi,
  fetchConversionMetricsApi,
  fetchRevenueMetricsApi,
  fetchRetentionMetricsApi,
  fetchSessionMetricsApi,
  fetchPlatformHealthMetricsApi,
  fetchSystemGeneratedMetricsApi,
  fetchDateWiseSystemMetricsApi,
} from "../../services/dashboardMatrix";
import { calculateCustomMetricValue, calculateCustomMetricForDate } from "../../utils/customMetricCalculator";
import { toastMessage } from "../../utils/toastMessage";
import CustomMetricsBuilder from "../../components/CustomMetricsBuilder/CustomMetricsBuilder";
import MetricManager from "../../components/MetricManager/MetricManager";
import DynamicSystemMetricsChart from "../../components/charts/DynamicSystemMetricsChart";
import SettingsIcon from "@mui/icons-material/Settings";
import ShowChartIcon from "@mui/icons-material/ShowChart";

const DashboardMatrixAnalytics = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)
  );
  const [endDate, setEndDate] = useState(new Date());
  const [metrics, setMetrics] = useState({
    userLifecycle: {},
    engagement: {},
    conversion: {},
    revenue: {},
    retention: {},
    session: {},
    platformHealth: {},
  });

  const [customMetrics, setCustomMetrics] = useState([]);
  const [systemMetrics, setSystemMetrics] = useState({});
  const [metricsConfig, setMetricsConfig] = useState([]);
  
  // Date-wise table data (used for both table and chart)
  const [dateWiseTableData, setDateWiseTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  
  // Table column selection states
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const [visibleTableColumns, setVisibleTableColumns] = useState({
    // User Lifecycle
    totalSignup: true,
    totalSignout: true,
    accountDeleted: true,
    totalActiveUsers: true,
    avgActiveTime: true,
    
    // Consultation
    firstConsultation: true,
    repeatConsultation: true,
    totalPaidChatConsultation: true,
    totalPaidCallConsultation: true,
    
    // Call Retention
    firstCallPaidConsultation: true,
    secondCallPaidConsultation: true,
    thirdCallPaidConsultation: true,
    fourthCallPaidConsultation: true,
    fifthCallPaidConsultation: true,
    sixthCallPaidConsultation: true,
    seventhCallPaidConsultation: true,
    
    // Chat Retention
    firstChatPaidConsultation: true,
    secondChatPaidConsultation: true,
    thirdChatPaidConsultation: true,
    fourthChatPaidConsultation: true,
    fifthChatPaidConsultation: true,
    sixthChatPaidConsultation: true,
    seventhChatPaidConsultation: true,
    
    // Free Chat Attempts
    freeCanceledChatConsultation: true,
    
    // Free Call Attempts
    freeCancelledCallConsultation: true,
    
    // Paid Call Issues
    paidMissedCallConsultation: true,
    paidMissedChatConsultation: true,
    
    // Paid Chat Attempts
    attemptedPaidChatConsultation: true,
    paidCancelledChatConsultation: true,
    
    // Paid Call Attempts
    attemptedPaidCallConsultation: true,
    paidCancelledCallConsultation: true,
    
    // Wallet & Revenue
    noOfFirstRecharge: true,
    totalNoOfRecharge: true,
    firstRechargeAmount: true,
    totalRechargeAmount: true,
    spendOnCall: true,
    spendOnChat: true,
    totalCoinsInWallet: true,
    
    // Time Metrics
    timeSpentOnChat: true,
    timeSpentOnCall: true,
  });

  // Custom metrics column visibility (dynamically added)
  const [visibleCustomMetricsColumns, setVisibleCustomMetricsColumns] = useState({});

  const tabs = [
    { label: "System Metrics", icon: <AssessmentIcon />, value: 0 },
    { label: "Custom Metrics", icon: <ChartIcon />, value: 8 },
    // { label: "Manage Metrics", icon: <SettingsIcon />, value: 9 },
  ];

  const fetchAllMetrics = async () => {
    try {
      setLoading(true);
      const dateParams = {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      };

      const [
        systemMetricsData,
        userLifecycle,
        engagement,
        conversion,
        revenue,
        retention,
        session,
        platformHealth,
      ] = await Promise.all([
        fetchSystemGeneratedMetricsApi(dateParams)
        ,[],[] , [],[],[],[],[]
        // fetchUserLifecycleMetricsApi(dateParams),
        // fetchEngagementMetricsApi(dateParams),
        // fetchConversionMetricsApi(dateParams),
        // fetchRevenueMetricsApi(dateParams),
        // fetchRetentionMetricsApi(dateParams),
        // fetchSessionMetricsApi(dateParams),
        // fetchPlatformHealthMetricsApi(dateParams),
      ]);

      setSystemMetrics(systemMetricsData?.data?.result || {});
      setMetrics({
        userLifecycle: userLifecycle?.data?.result || {},
        engagement: engagement?.data?.result || {},
        conversion: conversion?.data?.result || {},
        revenue: revenue?.data?.result || {},
        retention: retention?.data?.result || {},
        session: session?.data?.result || {},
        platformHealth: platformHealth?.data?.result || {},
      });
    } catch (error) {
      console.error("Error fetching metrics:", error);
      toastMessage("Error fetching metrics data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllMetrics();
  }, [startDate, endDate]);

  // Load custom metrics and initialize column visibility
  useEffect(() => {
    try {
      const savedCustomMetrics = localStorage.getItem("customMetrics");
      if (savedCustomMetrics) {
        const customMetricsList = JSON.parse(savedCustomMetrics);
        const customMetricsConfig = JSON.parse(localStorage.getItem("customMetricsConfig") || "[]");
        
        const visibleCustomMetrics = customMetricsList.filter((metric) => {
          const config = customMetricsConfig.find((cm) => cm.id === metric.id);
          return config ? config.visible !== false : true;
        });
        
        // Initialize custom metrics column visibility
        const initialVisibility = {};
        visibleCustomMetrics.forEach((metric) => {
          initialVisibility[`custom_${metric.id}`] = true;
        });
        setVisibleCustomMetricsColumns(initialVisibility);
        setCustomMetrics(visibleCustomMetrics);
      }
    } catch (error) {
      console.error("Error loading custom metrics:", error);
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRefresh = () => {
    fetchAllMetrics();
    handleMenuClose();
  };

  const handleExport = () => {
    // Export functionality
    toastMessage("Export feature coming soon", "info");
    handleMenuClose();
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    return Number(num).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });
  };

  // Format percentage
  const formatPercentage = (num) => {
    if (num === null || num === undefined) return "0%";
    return `${Number(num).toFixed(2)}%`;
  };

  // Format currency
  const formatCurrency = (num) => {
    if (num === null || num === undefined) return "₹0";
    return `₹${Number(num).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;
  };

  // Format time (seconds to readable format)
  const formatTime = (seconds) => {
    if (!seconds) return "0s";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds > 10 ? (seconds % 60).toFixed(2) : 0;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  // Map metric keys to FontAwesome icons
  const getFontAwesomeIconByMetricKey = (metricKey) => {
    const iconMap = {
      // User Lifecycle
      totalSignup: "fa-user-plus",
      totalSignout: "fa-sign-out",
      accountDeleted: "fa-user-times",
      totalActiveUsers: "fa-users",
      avgActiveTime: "fa-clock-o",
      
      // Consultation
      firstConsultation: "fa-handshake-o",
      repeatConsultation: "fa-repeat",
      totalPaidChatConsultation: "fa-comments",
      totalPaidCallConsultation: "fa-phone",
      
      // Call Retention
      firstCallPaidConsultation: "fa-phone",
      secondCallPaidConsultation: "fa-phone",
      thirdCallPaidConsultation: "fa-phone",
      fourthCallPaidConsultation: "fa-phone",
      fifthCallPaidConsultation: "fa-phone",
      sixthCallPaidConsultation: "fa-phone",
      seventhCallPaidConsultation: "fa-phone",
      
      // Chat Retention
      firstChatPaidConsultation: "fa-comments",
      secondChatPaidConsultation: "fa-comments",
      thirdChatPaidConsultation: "fa-comments",
      fourthChatPaidConsultation: "fa-comments",
      fifthChatPaidConsultation: "fa-comments",
      sixthChatPaidConsultation: "fa-comments",
      seventhChatPaidConsultation: "fa-comments",
      
      // Free Chat Attempts
      freeCanceledChatConsultation: "fa-comments",
      
      // Free Call Attempts
      freeCancelledCallConsultation: "fa-phone",
      
      // Paid Chat Attempts
      attemptedPaidChatConsultation: "fa-comments",
      paidCancelledChatConsultation: "fa-comments",
      
      // Paid Call Attempts
      attemptedPaidCallConsultation: "fa-phone",
      paidCancelledCallConsultation: "fa-phone",
      
      // Paid Call Issues
      paidMissedCallConsultation: "fa-phone",
      paidMissedChatConsultation: "fa-comments",
      
      // Wallet & Revenue
      noOfFirstRecharge: "fa-wallet",
      totalNoOfRecharge: "fa-wallet",
      firstRechargeAmount: "fa-money",
      totalRechargeAmount: "fa-wallet",
      spendOnCall: "fa-money",
      spendOnChat: "fa-money",
      totalCoinsInWallet: "fa-wallet",
      
      // Time Metrics
      timeSpentOnChat: "fa-clock-o",
      timeSpentOnCall: "fa-clock-o",
    };
    
    return iconMap[metricKey] || "fa-bar-chart";
  };

  // Metric Card Component - Using kpi-card design
  const MetricCard = ({ title, value, subtitle, icon, color = "#ff8989", trend, metricKey }) => {
    const faIcon = metricKey ? getFontAwesomeIconByMetricKey(metricKey) : "fa-bar-chart";
    
    return (
      <div className="kpi-card" style={{ borderLeftColor: color }}>
        <div className="kpi-icon" style={{ backgroundColor: color }}>
          <i className={`fa ${faIcon}`}></i>
        </div>
        <div className="kpi-content">
          <h3>{title}</h3>
          <p className="kpi-value">{value}</p>
          {subtitle && (
            <p style={{ fontSize: "0.75rem", color: "#999", margin: "4px 0 0 0" }}>
              {subtitle}
            </p>
          )}
          {trend && (
            <p style={{ 
              fontSize: "0.75rem", 
              color: trend.includes("+") ? "#4caf50" : trend.includes("-") ? "#f44336" : "#666",
              margin: "4px 0 0 0"
            }}>
              {trend}
            </p>
          )}
        </div>
      </div>
    );
  };


  // Load metrics configuration
  useEffect(() => {
    try {
      const saved = localStorage.getItem("systemMetricsConfig");
      if (saved) {
        setMetricsConfig(JSON.parse(saved));
      }
    } catch (error) {
      console.error("Error loading metrics config:", error);
    }
  }, []);

  // Get visible metrics based on configuration
  const getVisibleMetrics = () => {
    if (metricsConfig.length === 0) {
      // If no config, show all metrics
      return null;
    }
    return metricsConfig.filter((m) => m.visible).map((m) => m.key);
  };

  // Extract system metric keys from visible custom metric formulas
  const getSystemMetricsUsedInCustomMetrics = () => {
    try {
      const savedCustomMetrics = localStorage.getItem("customMetrics");
      const customMetricsConfig = JSON.parse(localStorage.getItem("customMetricsConfig") || "[]");
      
      if (!savedCustomMetrics) return new Set();
      
      const customMetricsList = JSON.parse(savedCustomMetrics);
      const visibleCustomMetrics = customMetricsList.filter((metric) => {
        const config = customMetricsConfig.find((cm) => cm.id === metric.id);
        return config ? config.visible !== false : true; // Default to visible if no config
      });
      
      // All possible system metric keys
      const allSystemMetricKeys = [
        "totalSignup", "totalSignout", "accountDeleted", "totalActiveUsers",
        "avgActiveTime", "firstConsultation", "repeatConsultation",
        "totalPaidChatConsultation", "totalPaidCallConsultation",
        "firstCallPaidConsultation", "secondCallPaidConsultation", "thirdCallPaidConsultation",
        "fourthCallPaidConsultation", "fifthCallPaidConsultation", "sixthCallPaidConsultation",
        "seventhCallPaidConsultation", "firstChatPaidConsultation", "secondChatPaidConsultation",
        "thirdChatPaidConsultation", "fourthChatPaidConsultation", "fifthChatPaidConsultation",
        "sixthChatPaidConsultation", "seventhChatPaidConsultation",
        "freeCanceledChatConsultation", "freeCancelledCallConsultation",
        "attemptedPaidChatConsultation", "attemptedPaidCallConsultation",
        "paidCancelledChatConsultation", "paidCancelledCallConsultation",
        "paidMissedCallConsultation", "paidMissedChatConsultation",
        "noOfFirstRecharge", "totalNoOfRecharge", "firstRechargeAmount", "totalRechargeAmount",
        "spendOnCall", "spendOnChat", "totalCoinsInWallet", 
        "timeSpentOnChat", "timeSpentOnCall"
      ];
      
      const usedKeys = new Set();
      
      visibleCustomMetrics.forEach((customMetric) => {
        if (customMetric.formula) {
          // Extract all system metric keys from the formula
          allSystemMetricKeys.forEach((key) => {
            // Use word boundary regex to match whole words only
            const regex = new RegExp(`\\b${key}\\b`, "g");
            if (regex.test(customMetric.formula)) {
              usedKeys.add(key);
            }
          });
        }
      });
      
      return usedKeys;
    } catch (error) {
      console.error("Error extracting system metrics from custom metrics:", error);
      return new Set();
    }
  };

  // Get visible custom metrics
  const getVisibleCustomMetrics = () => {
    try {
      const savedCustomMetrics = localStorage.getItem("customMetrics");
      const customMetricsConfig = JSON.parse(localStorage.getItem("customMetricsConfig") || "[]");
      
      if (!savedCustomMetrics) return [];
      
      const customMetricsList = JSON.parse(savedCustomMetrics);
      const visibleCustomMetrics = customMetricsList.filter((metric) => {
        const config = customMetricsConfig.find((cm) => cm.id === metric.id);
        return config ? config.visible !== false : true; // Default to visible if no config
      });
      
      return visibleCustomMetrics;
    } catch (error) {
      console.error("Error loading visible custom metrics:", error);
      return [];
    }
  };


  // Render System Generated Metrics Only (with visibility control)
  const renderSystemMetrics = () => {
    const sm = systemMetrics;
    const visibleKeys = getVisibleMetrics();
    const systemMetricsUsedInCustom = getSystemMetricsUsedInCustomMetrics();
    const visibleCustomMetrics = getVisibleCustomMetrics();
    
    // Metric definitions with icons and colors
    const metricDefinitions = {
      totalSignup: { label: "Total Signup", icon: <AccountIcon sx={{ color: "#ff8989" }} />, color: "#ff8989", category: "User Lifecycle" },
      totalSignout: { label: "Total Signout", icon: <AccountIcon sx={{ color: "#f44336" }} />, color: "#f44336", category: "User Lifecycle" },
      accountDeleted: { label: "Account Deleted", icon: <AccountIcon sx={{ color: "#e91e63" }} />, color: "#e91e63", category: "User Lifecycle" },
      totalActiveUsers: { label: "Total Active Users", icon: <PeopleIcon sx={{ color: "#1976d2" }} />, color: "#1976d2", category: "User Lifecycle" },
      avgActiveTime: { label: "Avg. Active Time", icon: <TimeIcon sx={{ color: "#ff9800" }} />, color: "#ff9800", category: "User Lifecycle" },
      firstConsultation: { label: "First Consultation", icon: <ChatIcon sx={{ color: "#4caf50" }} />, color: "#4caf50", category: "Consultation" },
      repeatConsultation: { label: "Repeat Consultation", icon: <PhoneIcon sx={{ color: "#2196f3" }} />, color: "#2196f3", category: "Consultation" },
      totalPaidChatConsultation: { label: "Total Paid Chat Consultation", icon: <ChatIcon sx={{ color: "#ff9800" }} />, color: "#ff9800", category: "Consultation" },
      totalPaidCallConsultation: { label: "Total Paid Call Consultation", icon: <PhoneIcon sx={{ color: "#ff8989" }} />, color: "#ff8989", category: "Consultation" },
      firstCallPaidConsultation: { label: "1st Call Paid Consultation", icon: <PhoneIcon sx={{ color: "#ff8989" }} />, color: "#ff8989", category: "Call Retention" },
      secondCallPaidConsultation: { label: "2nd Call Paid Consultation", icon: <PhoneIcon sx={{ color: "#ff8989" }} />, color: "#ff8989", category: "Call Retention" },
      thirdCallPaidConsultation: { label: "3rd Call Paid Consultation", icon: <PhoneIcon sx={{ color: "#ff8989" }} />, color: "#ff8989", category: "Call Retention" },
      fourthCallPaidConsultation: { label: "4th Call Paid Consultation", icon: <PhoneIcon sx={{ color: "#ff8989" }} />, color: "#ff8989", category: "Call Retention" },
      fifthCallPaidConsultation: { label: "5th Call Paid Consultation", icon: <PhoneIcon sx={{ color: "#ff8989" }} />, color: "#ff8989", category: "Call Retention" },
      sixthCallPaidConsultation: { label: "6th Call Paid Consultation", icon: <PhoneIcon sx={{ color: "#ff8989" }} />, color: "#ff8989", category: "Call Retention" },
      seventhCallPaidConsultation: { label: "7th Call Paid Consultation", icon: <PhoneIcon sx={{ color: "#ff8989" }} />, color: "#ff8989", category: "Call Retention" },
      firstChatPaidConsultation: { label: "1st Chat Paid Consultation", icon: <ChatIcon sx={{ color: "#4caf50" }} />, color: "#4caf50", category: "Chat Retention" },
      secondChatPaidConsultation: { label: "2nd Chat Paid Consultation", icon: <ChatIcon sx={{ color: "#4caf50" }} />, color: "#4caf50", category: "Chat Retention" },
      thirdChatPaidConsultation: { label: "3rd Chat Paid Consultation", icon: <ChatIcon sx={{ color: "#4caf50" }} />, color: "#4caf50", category: "Chat Retention" },
      fourthChatPaidConsultation: { label: "4th Chat Paid Consultation", icon: <ChatIcon sx={{ color: "#4caf50" }} />, color: "#4caf50", category: "Chat Retention" },
      fifthChatPaidConsultation: { label: "5th Chat Paid Consultation", icon: <ChatIcon sx={{ color: "#4caf50" }} />, color: "#4caf50", category: "Chat Retention" },
      sixthChatPaidConsultation: { label: "6th Chat Paid Consultation", icon: <ChatIcon sx={{ color: "#4caf50" }} />, color: "#4caf50", category: "Chat Retention" },
      seventhChatPaidConsultation: { label: "7th Chat Paid Consultation", icon: <ChatIcon sx={{ color: "#4caf50" }} />, color: "#4caf50", category: "Chat Retention" },
      freeCanceledChatConsultation: { label: "Free Canceled Chat Consultation", icon: <ChatIcon sx={{ color: "#ff9800" }} />, color: "#ff9800", category: "Free Chat Attempts" },
      freeCancelledCallConsultation: { label: "Free Cancelled Call Consultation", icon: <PhoneIcon sx={{ color: "#ff9800" }} />, color: "#ff9800", category: "Free Call Attempts" },
      attemptedPaidChatConsultation: { label: "Attempted Paid Chat Consultation", icon: <ChatIcon sx={{ color: "#9c27b0" }} />, color: "#9c27b0", category: "Paid Chat Attempts" },
      paidCancelledChatConsultation: { label: "Paid Cancelled Chat Consultation", icon: <ChatIcon sx={{ color: "#f44336" }} />, color: "#f44336", category: "Paid Chat Attempts" },
      attemptedPaidCallConsultation: { label: "Attempted Paid Call Consultation", icon: <PhoneIcon sx={{ color: "#9c27b0" }} />, color: "#9c27b0", category: "Paid Call Attempts" },
      paidCancelledCallConsultation: { label: "Paid Cancelled Call Consultation", icon: <PhoneIcon sx={{ color: "#f44336" }} />, color: "#f44336", category: "Paid Call Attempts" },
      paidMissedCallConsultation: { label: "Paid Missed Call Consultation", icon: <PhoneIcon sx={{ color: "#e91e63" }} />, color: "#e91e63", category: "Paid Call Issues" },
      paidMissedChatConsultation: { label: "Paid Missed Chat Consultation", icon: <ChatIcon sx={{ color: "#e91e63" }} />, color: "#e91e63", category: "Paid Call Issues" },
      noOfFirstRecharge: { label: "No. of First Recharge", icon: <WalletIcon sx={{ color: "#2196f3" }} />, color: "#2196f3", category: "Wallet & Revenue" },
      totalNoOfRecharge: { label: "Total No. of Recharge", icon: <WalletIcon sx={{ color: "#03a9f4" }} />, color: "#03a9f4", category: "Wallet & Revenue" },
      firstRechargeAmount: { label: "First Recharge Amount", icon: <MoneyIcon sx={{ color: "#00bcd4" }} />, color: "#00bcd4", category: "Wallet & Revenue" },
      totalRechargeAmount: { label: "Total Recharge Amount", icon: <MoneyIcon sx={{ color: "#ff8989" }} />, color: "#ff8989", category: "Wallet & Revenue" },
      spendOnCall: { label: "Spend on Call", icon: <MoneyIcon sx={{ color: "#ff9800" }} />, color: "#ff9800", category: "Wallet & Revenue" },
      spendOnChat: { label: "Spend on Chat", icon: <MoneyIcon sx={{ color: "#ff5722" }} />, color: "#ff5722", category: "Wallet & Revenue" },
      totalCoinsInWallet: { label: "Total Coins in Wallet", icon: <WalletIcon sx={{ color: "#9c27b0" }} />, color: "#9c27b0", category: "Wallet & Revenue" },
      timeSpentOnChat: { label: "time spent on chat", icon: <ChatIcon sx={{ color: "#4caf50" }} />, color: "#4caf50", category: "Time Metrics" },
      timeSpentOnCall: { label: "time spent on Call", icon: <PhoneIcon sx={{ color: "#ff8989" }} />, color: "#ff8989", category: "Time Metrics" },
    };

    // Filter metrics based on visibility config AND exclude those used in visible custom metrics
    const metricsToShow = (visibleKeys
      ? Object.keys(metricDefinitions).filter((key) => visibleKeys.includes(key))
      : Object.keys(metricDefinitions)
    ).filter((key) => !systemMetricsUsedInCustom.has(key)); // Exclude metrics used in custom metrics

    // Create flat list of all visible system metrics (not grouped by category)
    const allVisibleSystemMetrics = metricsToShow.map((key) => {
      const def = metricDefinitions[key];
      return def ? { key, ...def, value: sm[key] } : null;
    }).filter(Boolean);

    const formatValue = (key, value) => {
      if (value === null || value === undefined) return "0";
      if (key.includes("Spend") || key.includes("spend") || key.includes("RechargeAmount")) {
        return formatCurrency(value);
      }
      if (key.includes("Time") || key.includes("time") || key.includes("ActiveTime")) {
        return formatTime(value);
      }
      return formatNumber(value);
    };

    const formatCustomMetricValue = (value, formula) => {
      if (value === null || value === undefined) return "0";
      // Try to detect if it's a percentage, currency, or time based on formula
      if (formula && (formula.includes("* 100") || formula.includes("/") && formula.includes("%"))) {
        return formatPercentage(value);
      }
      if (formula && (formula.includes("spend") || formula.includes("Spend"))) {
        return formatCurrency(value);
      }
      if (formula && (formula.includes("time") || formula.includes("Time"))) {
        return formatTime(value);
      }
      return formatNumber(value);
    };

 

    // Combine all visible metrics (system + custom) into a single array
    const allVisibleMetrics = [
      ...allVisibleSystemMetrics.map((metric) => ({
        type: 'system',
        ...metric
      })),
      ...visibleCustomMetrics.map((customMetric) => ({
        type: 'custom',
        id: customMetric.id,
        name: customMetric.name,
        description: customMetric.description,
        formula: customMetric.formula,
        value: calculateCustomMetricValue(customMetric.formula, sm),
        color: "#ff8989",
      }))
    ];

    return (
      <Box>
        {allVisibleMetrics.length === 0 ? (
          <Card sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" sx={{ color: "#666", mb: 2 }}>
              No metrics visible
            </Typography>
            <Typography variant="body2" sx={{ color: "#999" }}>
              Go to "Manage Metrics" tab to show metrics
            </Typography>
          </Card>
        ) : (
          <>

          {/* System Metrics Cards */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#ff8989" }}>System Metrics</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => setActiveTab(2)}
              sx={{
                color: "#ff8989",
                borderColor: "#ff8989",
                "&:hover": {
                  borderColor: "#ff8989",
                  backgroundColor: "#ff898915",
                },
              }}
            >
              Manage Metrics
            </Button>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: "var(--spacing-xl)",
              marginBottom: "var(--spacing-3xl)",
            }}
          >
            {allVisibleMetrics.map((metric) => {
              if (metric.type === 'custom') {
                return (
                  <div 
                    key={`custom_${metric.id}`}
                    className="kpi-card" 
                    style={{ borderLeftColor: "#ff8989", borderLeftWidth: "4px" }}
                  >
                    <div className="kpi-icon" style={{ backgroundColor: "#ff8989" }}>
                      <i className="fa fa-line-chart"></i>
                    </div>
                    <div className="kpi-content">
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                        <h3 style={{ margin: 0 }}>{metric.name}</h3>
                        {metric.description && (
                          <Tooltip title={metric.description} arrow placement="top">
                            <IconButton
                              size="small"
                              sx={{
                                padding: "2px",
                                color: "#999",
                                "&:hover": {
                                  color: "#ff8989",
                                },
                              }}
                            >
                              <InfoIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {metric.formula && (
                          <Tooltip 
                            title={
                              <Box sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                                Formula: {metric.formula}
                              </Box>
                            } 
                            arrow 
                            placement="top"
                          >
                            <IconButton
                              size="small"
                              sx={{
                                padding: "2px",
                                color: "#999",
                                "&:hover": {
                                  color: "#ff8989",
                                },
                              }}
                            >
                              <InfoIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <p className="kpi-value">
                        {formatCustomMetricValue(metric.value, metric.formula)}
                      </p>
                    </div>
                  </div>
                );
              } else {
                // System metric
                return (
                  <MetricCard
                    key={metric.key}
                    title={metric.label}
                    value={formatValue(metric.key, metric.value)}
                    icon={metric.icon}
                    color={metric.color}
                    metricKey={metric.key}
                  />
                );
              }
            })}
          </Box>
          </>
        )}
      </Box>
    );
  };

  // Column definitions for table
  const tableColumnDefinitions = {
    // User Lifecycle
    totalSignup: { category: 'User Lifecycle', label: 'Total Signup', type: 'number' },
    totalSignout: { category: 'User Lifecycle', label: 'Total Signout', type: 'number' },
    accountDeleted: { category: 'User Lifecycle', label: 'Account Deleted', type: 'number' },
    totalActiveUsers: { category: 'User Lifecycle', label: 'Total Active Users', type: 'number' },
    avgActiveTime: { category: 'User Lifecycle', label: 'Avg. Active Time', type: 'time' },
    
    // Consultation
    firstConsultation: { category: 'Consultation', label: 'First Consultation', type: 'number' },
    repeatConsultation: { category: 'Consultation', label: 'Repeat Consultation', type: 'number' },
    totalPaidChatConsultation: { category: 'Consultation', label: 'Total Paid Chat Consultation', type: 'number' },
    totalPaidCallConsultation: { category: 'Consultation', label: 'Total Paid Call Consultation', type: 'number' },
    
    // Call Retention
    firstCallPaidConsultation: { category: 'Call Retention', label: '1st Call Paid Consultation', type: 'number' },
    secondCallPaidConsultation: { category: 'Call Retention', label: '2nd Call Paid Consultation', type: 'number' },
    thirdCallPaidConsultation: { category: 'Call Retention', label: '3rd Call Paid Consultation', type: 'number' },
    fourthCallPaidConsultation: { category: 'Call Retention', label: '4th Call Paid Consultation', type: 'number' },
    fifthCallPaidConsultation: { category: 'Call Retention', label: '5th Call Paid Consultation', type: 'number' },
    sixthCallPaidConsultation: { category: 'Call Retention', label: '6th Call Paid Consultation', type: 'number' },
    seventhCallPaidConsultation: { category: 'Call Retention', label: '7th Call Paid Consultation', type: 'number' },
    
    // Chat Retention
    firstChatPaidConsultation: { category: 'Chat Retention', label: '1st Chat Paid Consultation', type: 'number' },
    secondChatPaidConsultation: { category: 'Chat Retention', label: '2nd Chat Paid Consultation', type: 'number' },
    thirdChatPaidConsultation: { category: 'Chat Retention', label: '3rd Chat Paid Consultation', type: 'number' },
    fourthChatPaidConsultation: { category: 'Chat Retention', label: '4th Chat Paid Consultation', type: 'number' },
    fifthChatPaidConsultation: { category: 'Chat Retention', label: '5th Chat Paid Consultation', type: 'number' },
    sixthChatPaidConsultation: { category: 'Chat Retention', label: '6th Chat Paid Consultation', type: 'number' },
    seventhChatPaidConsultation: { category: 'Chat Retention', label: '7th Chat Paid Consultation', type: 'number' },
    
    // Free Chat Attempts
    freeCancelledChatConsultation: { category: 'Free Chat Attempts', label: 'Free Cancelled Chat Consultation', type: 'number' },
    
    // Free Call Attempts
    freeCancelledCallConsultation: { category: 'Free Call Attempts', label: 'Free Cancelled Call Consultation', type: 'number' },
    
    // Paid Chat Attempts
    attemptedPaidChatConsultation: { category: 'Paid Chat Attempts', label: 'Attempted Paid Chat Consultation', type: 'number' },
    paidCancelledChatConsultation: { category: 'Paid Chat Attempts', label: 'Paid Cancelled Chat Consultation', type: 'number' },
    
    // Paid Call Attempts
    attemptedPaidCallConsultation: { category: 'Paid Call Attempts', label: 'Attempted Paid Call Consultation', type: 'number' },
    paidCancelledCallConsultation: { category: 'Paid Call Attempts', label: 'Paid Cancelled Call Consultation', type: 'number' },
    
    // Paid Call Issues
    paidMissedCallConsultation: { category: 'Paid Call Issues', label: 'Paid Missed Call Consultation', type: 'number' },
    paidMissedChatConsultation: { category: 'Paid Call Issues', label: 'Paid Missed Chat Consultation', type: 'number' },
    
    // Wallet & Revenue
    noOfFirstRecharge: { category: 'Wallet & Revenue', label: 'No. of First Recharge', type: 'number' },
    totalNoOfRecharge: { category: 'Wallet & Revenue', label: 'Total No. of Recharge', type: 'number' },
    firstRechargeAmount: { category: 'Wallet & Revenue', label: 'First Recharge Amount', type: 'currency' },
    totalRechargeAmount: { category: 'Wallet & Revenue', label: 'Total Recharge Amount', type: 'currency' },
    spendOnCall: { category: 'Wallet & Revenue', label: 'Spend on Call', type: 'currency' },
    spendOnChat: { category: 'Wallet & Revenue', label: 'Spend on Chat', type: 'currency' },
    totalCoinsInWallet: { category: 'Wallet & Revenue', label: 'Total Coins in Wallet', type: 'number' },
    
    // Time Metrics
    timeSpentOnChat: { category: 'Time Metrics', label: 'time spent on chat', type: 'time' },
    timeSpentOnCall: { category: 'Time Metrics', label: 'time spent on Call', type: 'time' },
  };

  // Create stable string representations of dependencies to avoid infinite loops
  const visibleColumnsString = useMemo(() => {
    const visibleColumns = Object.keys(tableColumnDefinitions)
      .filter(key => visibleTableColumns[key] === true)
      .sort()
      .join(",");
    const visibleCustomIds = customMetrics
      .filter(metric => visibleCustomMetricsColumns[`custom_${metric.id}`] === true)
      .map(metric => metric.id)
      .sort()
      .join(",");
    return `${visibleColumns}|${visibleCustomIds}|${customMetrics.length}`;
  }, [visibleTableColumns, visibleCustomMetricsColumns, customMetrics]);

  // Use ref to track previous values and prevent unnecessary API calls
  const prevValuesRef = useRef({
    startDate: null,
    endDate: null,
    visibleColumnsString: null
  });

  // Use refs to store latest values without causing re-renders
  const visibleTableColumnsRef = useRef(visibleTableColumns);
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);

  // Update refs when values change
  useEffect(() => {
    visibleTableColumnsRef.current = visibleTableColumns;
  }, [visibleTableColumns]);

  useEffect(() => {
    startDateRef.current = startDate;
  }, [startDate]);

  useEffect(() => {
    endDateRef.current = endDate;
  }, [endDate]);

  // Fetch date-wise data for table (with ALL columns for chart use)
  useEffect(() => {
    const startDateStr = startDate?.toISOString();
    const endDateStr = endDate?.toISOString();
    
    // Only fetch if values actually changed
    if (
      prevValuesRef.current.startDate !== startDateStr ||
      prevValuesRef.current.endDate !== endDateStr ||
      prevValuesRef.current.visibleColumnsString !== visibleColumnsString
    ) {
      prevValuesRef.current = {
        startDate: startDateStr,
        endDate: endDateStr,
        visibleColumnsString: visibleColumnsString
      };

      // Fetch data using refs to get latest values
      const fetchData = async () => {
        try {
          setTableLoading(true);
          
          // Get ALL columns for chart (chart needs all columns)
          // Table will filter to show only visible columns
          const allColumns = Object.keys(tableColumnDefinitions);
          
          if (allColumns.length === 0) {
            setDateWiseTableData([]);
            setTableLoading(false);
            return;
          }
          
          // Prepare custom metrics with formulas for backend calculation
          const customMetricsPayload = customMetrics.map(metric => ({
            id: metric.id,
            name: metric.name,
            formula: metric.formula
          }));
          
          const dateParams = {
            startDate: startDateRef.current?.toISOString(),
            endDate: endDateRef.current?.toISOString(),
            metrics: allColumns.join(","), // Send all metrics for chart
            customMetrics: JSON.stringify(customMetricsPayload), // Send full custom metrics with formulas
          };

          const response = await fetchDateWiseSystemMetricsApi(dateParams);
          const data = response?.data?.result || [];
          setDateWiseTableData(data);
        } catch (error) {
          console.error("Error fetching date-wise table data:", error);
          setDateWiseTableData([]);
        } finally {
          setTableLoading(false);
        }
      };

      fetchData();
    }
  }, [startDate, endDate, visibleColumnsString]);

  // Get visible columns count
  const getVisibleTableColumnCount = () => {
    const systemCount = Object.values(visibleTableColumns).filter(Boolean).length;
    const customCount = Object.values(visibleCustomMetricsColumns).filter(Boolean).length;
    return systemCount + customCount;
  };

  // Get all columns grouped by category
  const getAllTableColumnsByCategory = () => {
    const categories = {};
    
    // Add system metrics
    Object.keys(tableColumnDefinitions).forEach((columnKey) => {
      const category = tableColumnDefinitions[columnKey].category;
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push({
        key: columnKey,
        ...tableColumnDefinitions[columnKey]
      });
    });
    
    // Add custom metrics
    if (customMetrics.length > 0) {
      categories['Custom Metrics'] = customMetrics.map(metric => ({
        key: `custom_${metric.id}`,
        label: metric.name,
        type: 'number',
        category: 'Custom Metrics',
        description: metric.description,
        formula: metric.formula
      }));
    }
    
    return categories;
  };

  // Handle column toggle
  const handleTableColumnToggle = (columnKey) => {
    if (columnKey.startsWith('custom_')) {
      setVisibleCustomMetricsColumns(prev => ({
        ...prev,
        [columnKey]: !prev[columnKey]
      }));
    } else {
      setVisibleTableColumns(prev => ({
        ...prev,
        [columnKey]: !prev[columnKey]
      }));
    }
  };

  // Handle select all columns
  const handleSelectAllTableColumns = () => {
    const allColumns = {};
    Object.keys(tableColumnDefinitions).forEach(key => {
      allColumns[key] = true;
    });
    setVisibleTableColumns(prev => ({ ...prev, ...allColumns }));
  };

  // Handle deselect all columns
  const handleDeselectAllTableColumns = () => {
    const noColumns = {};
    Object.keys(tableColumnDefinitions).forEach(key => {
      noColumns[key] = false;
    });
    setVisibleTableColumns(prev => ({ ...prev, ...noColumns }));
  };

  // Handle select all in category
  const handleSelectAllInTableCategory = (category) => {
    setVisibleTableColumns(prev => {
      const updated = { ...prev };
      Object.keys(tableColumnDefinitions).forEach(key => {
        if (tableColumnDefinitions[key].category === category) {
          updated[key] = true;
        }
      });
      return updated;
    });
  };

  // Handle deselect all in category
  const handleDeselectAllInTableCategory = (category) => {
    setVisibleTableColumns(prev => {
      const updated = { ...prev };
      Object.keys(tableColumnDefinitions).forEach(key => {
        if (tableColumnDefinitions[key].category === category) {
          updated[key] = false;
        }
      });
      return updated;
    });
  };

  // Render cell value based on type
  const renderTableCellValue = (value, type) => {
    if (value === null || value === undefined) return "0";
    
    switch (type) {
      case 'currency':
        return formatCurrency(value);
      case 'time':
        return formatTime(value);
      case 'number':
      default:
        return formatNumber(value);
    }
  };

  // Render System Metrics Table (Date-wise)
  const renderSystemMetricsTable = () => {
    // Get visible system metric columns (independent from card visibility)
    const systemColumnsToShow = Object.keys(tableColumnDefinitions).filter(key => {
      return visibleTableColumns[key] === true;
    });

    // Get visible custom metric columns
    const visibleCustomMetrics = customMetrics.filter(metric => {
      return visibleCustomMetricsColumns[`custom_${metric.id}`] === true;
    });

    const allColumnsToShow = [...systemColumnsToShow, ...visibleCustomMetrics.map(m => `custom_${m.id}`)];

    if (allColumnsToShow.length === 0) {
      return (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ color: "#666", mb: 2 }}>
            No columns selected
          </Typography>
          <Typography variant="body2" sx={{ color: "#999" }}>
            Use "Customize Columns" to select metrics to display
          </Typography>
        </Card>
      );
    }

    return (
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#ff8989" }}>
              System Metrics Table (Date-wise)
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip 
                label={`${getVisibleTableColumnCount()} columns selected`}
                color="primary"
                variant="outlined"
                size="small"
              />
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={() => setColumnSelectorOpen(true)}
                sx={{
                  borderColor: "#ff8989",
                  color: "#ff8989",
                  "&:hover": { borderColor: "#ff7070", backgroundColor: "#ff898915" },
                }}
              >
                Customize Columns
              </Button>
            </Box>
          </Box>

          {tableLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress sx={{ color: "#ff8989" }} />
            </Box>
          ) : dateWiseTableData.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" sx={{ color: "#666", mb: 2 }}>
                No data available
              </Typography>
              <Typography variant="body2" sx={{ color: "#999" }}>
                No date-wise data found for the selected date range
              </Typography>
            </Card>
          ) : (
            <>
              <div 
                className="table-container"
                style={{
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: "70vh",
                  maxWidth: "100%"
                }}
              >
                <table className="daily-breakdown-table">
                  <thead style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#ff8989" }}>
                    <tr>
                      <th style={{ position: "sticky", left: 0, zIndex: 11, backgroundColor: "#ff8989" }}>Date</th>
                      {systemColumnsToShow.map((key) => {
                        const colDef = tableColumnDefinitions[key];
                        return <th key={key}>{colDef.label}</th>;
                      })}
                      {visibleCustomMetrics.map((metric) => (
                        <th key={`custom_${metric.id}`}>{metric.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dateWiseTableData.length > 0 ? (
                      dateWiseTableData.map((row, index) => (
                        <tr key={row.date || index}>
                          <td 
                            className="date-cell"
                            style={{ 
                              position: "sticky", 
                              left: 0, 
                              zIndex: 8,
                              backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa"
                            }}
                          >
                            <div className="date-info">
                              <span className="date-text">
                                {row.date ? new Date(row.date).toLocaleDateString('en-GB', { 
                                  day: '2-digit', 
                                  month: 'short', 
                                  year: 'numeric' 
                                }) : "N/A"}
                              </span>
                            </div>
                          </td>
                          {systemColumnsToShow.map((key) => {
                            const colDef = tableColumnDefinitions[key];
                            const value = row[key] || 0;
                            const cellClass = colDef.type === 'currency' ? 'currency-cell' : 
                                            colDef.type === 'time' ? 'number-cell' : 'number-cell';
                            return (
                              <td key={key} className={cellClass}>
                                {renderTableCellValue(value, colDef.type)}
                              </td>
                            );
                          })}
                          {visibleCustomMetrics.map((metric) => {
                            const value = calculateCustomMetricForDate(metric.formula, row);
                            return (
                              <td key={`custom_${metric.id}`} className="number-cell">
                                {renderTableCellValue(value, 'number')}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={allColumnsToShow.length + 1} className="no-data">
                          <div className="no-data-content">
                            <i className="fa fa-chart-bar"></i>
                            <p>No data available for the selected period</p>
                            <small>Try selecting a different time range</small>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {dateWiseTableData.length > 0 && (
                    <tfoot style={{ position: "sticky", bottom: 0, zIndex: 9 }}>
                      <tr className="totals-row">
                        <td 
                          className="totals-label"
                          style={{ 
                            position: "sticky", 
                            left: 0, 
                            zIndex: 10,
                            backgroundColor: "#f5f5f5"
                          }}
                        >
                          TOTALS
                        </td>
                        {systemColumnsToShow.map((key) => {
                          const colDef = tableColumnDefinitions[key];
                          const total = dateWiseTableData.reduce((sum, row) => {
                            return sum + (parseFloat(row[key]) || 0);
                          }, 0);
                          const cellClass = colDef.type === 'currency' ? 'totals-currency' : 'totals-value';
                          return (
                            <td key={key} className={cellClass}>
                              {renderTableCellValue(total, colDef.type)}
                            </td>
                          );
                        })}
                        {visibleCustomMetrics.map((metric) => {
                          const total = dateWiseTableData.reduce((sum, row) => {
                            return sum + calculateCustomMetricForDate(metric.formula, row);
                          }, 0);
                          return (
                            <td key={`custom_${metric.id}`} className="totals-value">
                              {renderTableCellValue(total, 'number')}
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderSystemMetrics();
      case 1:
        return (
          <CustomMetricsBuilder
            startDate={startDate}
            endDate={endDate}
            onMetricsUpdate={setCustomMetrics}
          />
        );
      case 2:
        return (
          <MetricManager
            systemMetrics={systemMetrics}
            customMetrics={customMetrics}
            onMetricsConfigUpdate={(config) => {
              if (config.system) setMetricsConfig(config.system);
              if (config.custom) {
                // Update custom metrics visibility in localStorage
                const savedCustomMetrics = localStorage.getItem("customMetrics");
                if (savedCustomMetrics) {
                  const customMetricsList = JSON.parse(savedCustomMetrics);
                  const updated = customMetricsList.map((metric) => {
                    const configMetric = config.custom.find((cm) => cm.id === metric.id);
                    return configMetric ? { ...metric, visible: configMetric.visible } : metric;
                  });
                  localStorage.setItem("customMetrics", JSON.stringify(updated));
                }
              }
              // Reload to apply changes
              setTimeout(() => {
                window.location.reload();
              }, 500);
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "#ff8989",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                Dashboard Matrix Analytics
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#666", mt: 0.5, fontSize: "0.875rem" }}
              >
                Comprehensive metrics and insights for your platform
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Box
                display="flex"
                gap={1}
                sx={{
                  "& .react-datepicker-wrapper": {
                    width: "auto",
                  },
                  "& input": {
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "0.875rem",
                    width: "140px",
                  },
                }}
              >
                <ReactDatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  dateFormat="dd-MMM-yyyy"
                  placeholderText="Start Date"
                  maxDate={endDate}
                />
                <ReactDatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  dateFormat="dd-MMM-yyyy"
                  placeholderText="End Date"
                  minDate={startDate}
                  maxDate={new Date()}
                />
              </Box>
              <IconButton onClick={handleMenuOpen} sx={{ color: "#ff8989" }}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleRefresh}>
                  <RefreshIcon sx={{ mr: 1 }} />
                  Refresh Data
                </MenuItem>
                <MenuItem onClick={handleExport}>
                  <DownloadIcon sx={{ mr: 1 }} />
                  Export Report
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* Tabs */}
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTab-root": {
                  minHeight: 56,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  "&.Mui-selected": {
                    color: "#ff8989",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#ff8989",
                  height: 3,
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Card>
        </Box>

        {/* Content */}
        <Box sx={{ mt: 3 }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
            >
              <CircularProgress sx={{ color: "#ff8989" }} />
            </Box>
          ) : (
            <>
              {renderTabContent()}
              {/* Dynamic Chart - Always visible below the content */}
              {activeTab === 0 && (
                <>
                 
                  {/* System Metrics Table */}
                  <Box sx={{ mt: 4 }}>
                    {renderSystemMetricsTable()}
                  </Box>
                  <Box sx={{ mt: 4 }}>
                    <DynamicSystemMetricsChart
                      startDate={startDate}
                      endDate={endDate}
                      chartData={dateWiseTableData}
                      tableLoading={tableLoading}
                      tableColumnDefinitions={tableColumnDefinitions}
                      customMetrics={customMetrics}
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </Box>

        {/* Footer */}
        <Card sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Typography variant="body2" sx={{ color: "#666" }}>
                Last updated: {new Date().toLocaleString()}
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <Chip
                  label="Live Data"
                  size="small"
                  sx={{
                    backgroundColor: "#4caf5015",
                    color: "#4caf50",
                    fontWeight: 600,
                  }}
                />
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Auto-refresh: Every 30 seconds
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Column Selector Drawer */}
        <Drawer
          anchor="right"
          open={columnSelectorOpen}
          onClose={() => setColumnSelectorOpen(false)}
          PaperProps={{
            sx: { width: 400 }
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "#ff8989" }}>
              Customize Table Columns
            </Typography>
            
            {/* Global Actions */}
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSelectAllTableColumns}
                sx={{ mr: 1, borderColor: "#ff8989", color: "#ff8989" }}
              >
                Select All
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleDeselectAllTableColumns}
                sx={{ borderColor: "#ff8989", color: "#ff8989" }}
              >
                Deselect All
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="body2" sx={{ color: "#666" }}>
                {getVisibleTableColumnCount()} of {Object.keys(tableColumnDefinitions).length} columns selected
              </Typography>
              <Button
                variant="contained"
                onClick={() => setColumnSelectorOpen(false)}
                sx={{
                  backgroundColor: "#ff8989",
                  "&:hover": { backgroundColor: "#ff7070" },
                }}
              >
                Apply
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />

                  {/* Column Categories */}
                  {Object.entries(getAllTableColumnsByCategory()).map(([category, columns]) => (
                    <Box key={category} sx={{ mb: 4 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: category === 'Custom Metrics' ? '#ff8989' : 'inherit' }}>
                          {category}
                        </Typography>
                        <Box>
                          <Button
                            size="small"
                            onClick={() => handleSelectAllInTableCategory(category)}
                            sx={{ mr: 1, fontSize: "0.75rem" }}
                          >
                            All
                          </Button>
                          <Button
                            size="small"
                            onClick={() => handleDeselectAllInTableCategory(category)}
                            sx={{ fontSize: "0.75rem" }}
                          >
                            None
                          </Button>
                        </Box>
                      </Box>
                      
                      <List dense>
                        {columns.map((column) => {
                          const isCustom = column.key.startsWith('custom_');
                          const isChecked = isCustom 
                            ? visibleCustomMetricsColumns[column.key] 
                            : visibleTableColumns[column.key];
                          return (
                            <ListItem key={column.key} dense>
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={isChecked || false}
                                  onChange={() => handleTableColumnToggle(column.key)}
                                  size="small"
                                  sx={{
                                    color: isCustom ? "#ff8989" : "#ff8989",
                                    "&.Mui-checked": {
                                      color: "#ff8989",
                                    },
                                  }}
                                />
                              </ListItemIcon>
                              <ListItemText 
                                primary={
                                  <Box>
                                    <Typography variant="body2" sx={{ color: isCustom ? "#ff8989" : "inherit" }}>
                                      {column.label}
                                    </Typography>
                                    {column.description && (
                                      <Typography variant="caption" sx={{ color: "#999", display: "block", mt: 0.5 }}>
                                        {column.description}
                                      </Typography>
                                    )}
                                    {column.formula && (
                                      <Typography variant="caption" sx={{ color: "#999", fontFamily: "monospace", display: "block", fontSize: "0.7rem" }}>
                                        {column.formula}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                secondary={column.type}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  ))}
          </Box>
        </Drawer>
      </div>
    </div>
  );
};

export default DashboardMatrixAnalytics;

